import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError, Subject } from 'rxjs';
import { catchError, retry, map } from 'rxjs/operators';
import { AppStateService } from '../app-state/app-state.service';
import {
  SearchRequest,
  SearchResponse,
  SearchMethod,
  SearchResponseV3,
  SearchResultsProcessor,
} from './models/search.types';
import { API_BASE } from '../../constants/general.constants';

const DEFAULT_SEARCH_LIMIT = 10;

@Injectable({
  providedIn: 'root'
})
export class SearchService {
  private readonly http = inject(HttpClient);

  readonly #appState = inject(AppStateService);
  
  public $searchResultsSubject = new Subject<SearchResponse>();
  public lastSearchResults: SearchResponse | null = null;

  // Helper method to construct search-related URLs
  private createSearchUrl(resource?: string): string {
    let url = `${API_BASE}/search`;
    if (resource) {
      url += `/${resource}`;
    }
    return url;
  }

  private transformToV3Params(params: SearchRequest): any {
    // Map legacy names to new API names, preserving new names if already provided
    const projectSearch = params.projectSearch ?? false;
    const stepSearch = params.stepSearch ?? false;
    const projectId = params.projectId;
    const workflowId = params.workflowId;

    const { method, versions, query, offset, limit } = params;
    const result: any = { method, versions, query, projectSearch, stepSearch };
    if (typeof projectId !== 'undefined') result.projectId = projectId;
    if (typeof workflowId !== 'undefined') result.workflowId = workflowId as number;
    if (typeof offset !== 'undefined') result.offset = offset;
    if (typeof limit !== 'undefined') result.limit = limit;
    return result;
  }

  search(params: SearchRequest): Observable<SearchResponse> {
    const defaultParams: Partial<SearchRequest> = {
      method: 'like',
      versions: 'latest',
      offset: 0,
      limit: DEFAULT_SEARCH_LIMIT
    };

    const searchParams = this.transformToV3Params({ ...defaultParams, ...params } as SearchRequest);
    
    return this.http.post<SearchResponse | SearchResponseV3>(this.createSearchUrl(), searchParams).pipe(
      retry(1),
      map((response: any) => {
        // Accept both legacy and v3 shapes; normalize to legacy shape for existing consumers
        const normalized: SearchResponse =
          response && 'workflows' in response && 'steps' in response
            ? new SearchResultsProcessor().toLegacyResponse(response as SearchResponseV3)
            : (response as SearchResponse);

        this.lastSearchResults = normalized;
        this.$searchResultsSubject.next(normalized);
        return normalized;
      }),
      catchError(error => {
        console.error('Search failed:', error);
        return throwError(() => new Error(
          error.error?.message || 'Search failed. Please try again later.'
        ));
      })
    );
  }

  searchProjects(
    query: string, 
    method: SearchMethod = 'like',
    limit: number = DEFAULT_SEARCH_LIMIT
  ): Observable<SearchResponse> {
    return this.search({
      query,
      method,
      versions: this.#appState.isHeadVersion() ? 'head' : 'latest',
      projectSearch: true,
      stepSearch: false,
      limit
    });
  }

  searchProjectChunks(
    projectId: number, 
    query: string,
    workflowId?: number
  ): Observable<SearchResponse> {
    return this.search({
      query,
      method: 'like',
      versions: this.#appState.isHeadVersion() ? 'head' : 'latest',
      projectSearch: false,
      stepSearch: true,
      projectId: projectId,
      workflowId: workflowId
    });
  }

  /**
   * Search across all content types within a specific project
   */
  searchWithinProject(
    projectId: number,
    query: string,
    method: SearchMethod = 'like'
  ): Observable<SearchResponse> {
    return this.search({
      query,
      method,
      versions: this.#appState.isHeadVersion() ? 'head' : 'latest',
      projectSearch: true,
      stepSearch: true,
      projectId: projectId
    });
  }

  /**
   * Clear the last search results and notify subscribers
   */
  clearSearchResults(): void {
    this.lastSearchResults = null;
    this.$searchResultsSubject.next({
      projects: [],
      notebooks: [],
      chunks: []
    });
  }

  /**
   * Filter search results by rank threshold
   */
  filterResultsByRank(
    results: SearchResponse, 
    threshold: number = 0.5
  ): SearchResponse {
    return {
      projects: results.projects.filter(p => p.rank >= threshold),
      notebooks: results.notebooks,
      chunks: results.chunks.filter(c => c.rank >= threshold)
    };
  }
}
